import { useState, useEffect, useCallback, useMemo } from 'react';
import qs from 'query-string';
import Immutable from 'immutable';
import { useHistory, useLocation } from 'react-router';

import config from 'react-global-configuration';

import fromJSOrdered from '../../../helpers/fromJsOrdered';
import { DEFAULT_LANGUAGE } from '../../../config/constants';

const useQueryParameter = (param, defaultValue = '') => {
  const location = useLocation();
  const query = useMemo(() => qs.parse(location.search, { arrayFormat: 'brackets' }),
    [location.search],
  );
  return query[param] ?? defaultValue;
};

const useSearchApi = (searchIndex, searchPage = '/search', language = DEFAULT_LANGUAGE) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(Immutable.Map());
  const [selectedRecognitions, setSelectedRecognitions] = useState([]);

  const currentQuery = useQueryParameter('q', '');
  const filterParameter = 'filter[recognition_name][]';
  const currentRecognitionsQuery = useQueryParameter(filterParameter, []);
  const currentPage = useQueryParameter('page', 0);
  const API_URL = config.get('API_URL');

  const recognitionsKey = useMemo(() => {
    if (Array.isArray(currentRecognitionsQuery)) {
      return currentRecognitionsQuery.join(',');
    }
    return String(currentRecognitionsQuery);
  }, [currentRecognitionsQuery]);


  useEffect(() => {
    setLoading(true);

    const queryParams = {
      'filter[fulltext]': currentQuery,
      page: currentPage,
      count: 15,
      sort: 'title',
      order: 'ASC',
      operator: 'AND',
    };

    if (currentRecognitionsQuery && (Array.isArray(currentRecognitionsQuery)
      ? currentRecognitionsQuery.length > 0
      : currentRecognitionsQuery)) {
      queryParams[filterParameter] = currentRecognitionsQuery;
    }

    const queryString = getQueryString(queryParams);

    fetch(`${API_URL}/${language}/search_api/${searchIndex}/search?${queryString}`)
      .then((response) => response.json())
      .then((json) => {
        const ordered = fromJSOrdered(json);
        const immutableResults = Immutable.fromJS(ordered);

        setResults(immutableResults);
      })
      .catch(() => {
        setResults(Immutable.Map());
      })
      .finally(() => {
        setLoading(false);
      });
    // Don't add currentRecognitions to the depency array,
    // it will be end in an infinte loop because the query
    // will be an new object each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchIndex, currentQuery, recognitionsKey, currentPage, language, API_URL]);

  const doSearch = useCallback((_query, _page, _recognitions = []) => {
    const searchParams = {
      q: _query,
      page: _page,
      count: 15,
      operator: 'AND',
    };

    if (_recognitions && Boolean(_recognitions.length)) {
      searchParams[filterParameter] = _recognitions;
    }

    const queryString = getQueryString(searchParams);

    history.replace({
      pathname: searchPage,
      search: `?${queryString}`,
    });
  }, [history, searchPage]);

  return [
    results,
    currentQuery,
    loading,
    doSearch,
    selectedRecognitions,
    setSelectedRecognitions,
  ];
};

function getQueryString(params) {
  return qs.stringify(params, { arrayFormat: 'brackets' });
}

export default useSearchApi;
